// Brief 57 §1 — ONE derivation of "how many tasks are open", for every surface
// that puts a number on screen.
//
// Before this file there were TWO definitions, in files with no textual
// resemblance whatever — which is why no similarity measure could ever have
// found them, and why `docs/extraction-candidates.md` does not name this pair:
//
//   · the dashboard's, a WHITELIST — `["todo","inprogress","waiting"]
//     .includes(t.status)`, in src/views/Dashboard.tsx;
//   · both case lists', a BLACKLIST — `status !== "completed" && !==
//     "cancelled" && !== "archived"`, in src/views/CaseView.tsx and
//     src/views/CollectiveCaseView.tsx.
//
// They agree ONLY because `task_status` holds exactly those six codes, and
// that taxonomy is admin-editable (src/views/SettingsTaxonomies.tsx:48). The
// first firm to add a seventh gets a task that is open in the list and missing
// from the dashboard's count, silently, with no code change anywhere.
//
// ── THE FLAG, NOT THE LIST ───────────────────────────────────────────────────
//
// A status is terminal when ITS OWN TAXONOMY ROW SAYS SO — `meta.terminal`,
// edited in Settings → Taxonomies like every other meta field. So a seventh
// code cannot reintroduce the split in ANY surface, including surfaces not yet
// written: there is nothing to keep in sync, because there is no list.
//
// BUILTIN_TERMINAL_STATUSES below is the FALLBACK for a row that declares
// nothing — which is every row until
// server/scripts/seed_migration_57_task_status_terminal.js has run. So the
// answer is unchanged on the day this lands, and the seed is a tidy-up rather
// than a prerequisite. A firm that adds "awaiting client" gets an OPEN status
// with no deploy; a firm that adds a terminal one ticks one box.
//
// ── THE ONE BEHAVIOUR CHANGE, AND ITS MEASURED BLAST RADIUS ─────────────────
//
// A status with no taxonomy row at all is OPEN — including `null`. The
// dashboard's whitelist called such a task NOT open while both lists called it
// open, so a single definition has to move one of them, and this moves the
// dashboard onto the lists' answer. Measured 2026-08-09 across all four
// staging tenants: tenant 1 (watt) todo=109 · inprogress=13 · completed=18 and
// nothing else, tenants 3/4 no tasks at all, tenant 2 (canary) ONE task with
// status NULL. So: one row in the database, on the canary tenant.
//
// It also ends a disagreement INSIDE the dashboard, which is the part that
// argues the direction. Its OVERDUE counter already used the blacklist while
// its OPEN counter used the whitelist — so a null-status overdue task was
// counted as overdue and not as open, on one screen, from one array.
//
// ── WHAT IS DELIBERATELY STILL A LITERAL ────────────────────────────────────
//
// `inProgress` and `completedThisWeek` name ONE code each and are not
// open/closed questions: there is no flag for "this is the in-progress
// status", and `completedDate` is only ever set alongside `completed`
// (src/App.tsx clears it on any other transition). `task_status` is declared
// FIXED in server/src/lib/taxonomyClassification.js:68 and these are two of
// the bindings that declaration is about.

import { taskIsMine, viewerDueDate } from "./dueToday";
import { TASK_STATUS_TERMINAL } from "../utils/taxonomyMap";

// The three codes the six-code system taxonomy ships with that mean "over".
// Consulted ONLY for a status whose taxonomy row declares no `meta.terminal`.
//
// 60 §4c — DERIVED, not re-typed. The same six-code fact is now declared in the
// canon (`TASK_STATUS_TERMINAL`), because that is what the deploy seed emits
// into `meta.terminal`; a hand-written copy here would be a second declaration
// of one fact, and the day they disagreed the fallback and the row would give
// different answers for the same code with nothing to say so.
export const BUILTIN_TERMINAL_STATUSES: readonly string[] = Object.keys(
  TASK_STATUS_TERMINAL,
).filter((c) => TASK_STATUS_TERMINAL[c]);

export type CountableTask = {
  status?: string | null;
  dueDate?: string | null;
  completedDate?: string | null;
};

export type TaskCounts = {
  open: number;
  dueThisWeek: number;
  overdue: number;
  inProgress: number;
  completedThisWeek: number;
};

export type TaskCountOptions = {
  // null / omitted = EVERY viewer's tasks, which is what the two case lists
  // want and what the dashboard's all/My toggle passes when it says "all".
  viewerId?: number | null;
  // Raw TaxonomyEntry rows: useTaxonomy("task_status").entries. The TUPLE
  // shape useTaxonomyList returns carries no meta — src/lib/taxonomyHelpers.ts:97
  // says so — so passing tuples silently means "declare nothing" and every
  // code falls back. Safe, and the reason this reads `meta` defensively rather
  // than assuming a shape.
  statusEntries?: unknown;
  // Date SCALARS, passed in rather than derived. The dashboard computes
  // `today` in UTC and its week bounds in local time; deriving one from the
  // other here would change the answer between midnight and 02:00 Brussels
  // time, and this is an extraction (SESSION_RULES rule 3), not an
  // improvement.
  todayISO?: string | null;
  weekStartISO?: string | null;
  weekEndISO?: string | null;
};

// `meta.terminal`, read STRICTLY: only a real boolean is a declaration. A
// missing key, a null, or a truthy string means the row has not been ruled on
// and the fallback answers — the same posture as `meta.firmWide`
// (server/src/lib/eventTypeScope.js:25-34), for the same reason.
function declaredTerminal(entry: unknown): boolean | null {
  if (!entry || typeof entry !== "object" || Array.isArray(entry)) return null;
  const meta = (entry as { meta?: unknown }).meta;
  if (!meta || typeof meta !== "object" || Array.isArray(meta)) return null;
  const flag = (meta as { terminal?: unknown }).terminal;
  return typeof flag === "boolean" ? flag : null;
}

export function isTerminalStatus(
  status: string | null | undefined,
  statusEntries?: unknown,
): boolean {
  const code = typeof status === "string" ? status : "";
  if (Array.isArray(statusEntries)) {
    for (const entry of statusEntries) {
      if (!entry || typeof entry !== "object" || Array.isArray(entry)) continue;
      if ((entry as { code?: unknown }).code !== code) continue;
      const declared = declaredTerminal(entry);
      if (declared !== null) return declared;
      break; // the row exists and declares nothing — fall through to the set
    }
  }
  return BUILTIN_TERMINAL_STATUSES.includes(code);
}

// 57a-i / 67 §3 — THE OPEN CODES, AS A LIST, DERIVED FROM THE SAME FLAG.
//
// `src/App.tsx` wrote `const OPEN_ST = ["todo","inprogress","waiting"]` and
// threaded it as a prop into Dashboard, CompactColumn and CaseView, where it is
// asked `OPEN_ST.includes(t.status)`. That is a SECOND DECLARATION of the fact
// `isTerminalStatus` already owns, and on today's six codes the two agree only
// by accident: a firm that adds a seventh status gets a task that is open in
// one surface and not in another, silently, with no code change anywhere.
//
// A LIST, NOT THE PREDICATE, AND THAT IS DELIBERATE. Those call sites ask
// `.includes()`, which answers NO for a status the taxonomy does not carry —
// including `null`. `taskIsOpen` answers YES for the same row (see "THE ONE
// BEHAVIOUR CHANGE" above). Handing them the predicate would therefore move a
// null-status task into due-today and into the case task filter, which is an
// improvement and not this extraction (SESSION_RULES rule 3). What changes here
// is only where the list comes from: the taxonomy, instead of a literal.
//
// THE FALLBACK IS THE LADDER ITSELF, not a second hand-written open set. With
// no rows — the taxonomy has not loaded yet, or a caller passes tuples — the
// built-in six are filtered through the same predicate, so the answer is the
// literal that stood here and a boot flash cannot empty every list.
// THE TENANT'S LADDER, IN ITS OWN ORDER — the half of `openStatusCodes` that
// is not about terminality, lifted out because 136 §1 needs the same ladder
// under a different filter. Two readers of one fact, one derivation: a second
// copy of the "rows, else the built-in six" fallback is exactly the split this
// file's header was written about.
function ladderCodes(statusEntries?: unknown): string[] {
  const rows = Array.isArray(statusEntries) ? statusEntries : [];
  const codes = rows
    .map((r) =>
      r && typeof r === "object" && !Array.isArray(r)
        ? (r as { code?: unknown }).code
        : null,
    )
    .filter((c): c is string => typeof c === "string" && c !== "");
  return codes.length ? codes : Object.keys(TASK_STATUS_TERMINAL);
}

export function openStatusCodes(statusEntries?: unknown): string[] {
  return ladderCodes(statusEntries).filter(
    (c) => !isTerminalStatus(c, statusEntries),
  );
}

/* BRIEF 136 §1 [RULED 2026-08-25, Laurent] — WHAT THE STATUS PILL'S MENU MAY
   OFFER: "the status pill when clicked should show menu with all options, user
   to choose one, EXCEPT archived."

   THE LADDER, NOT A LIST. It is the firm's own `task_status` order, so a firm
   that adds a seventh status gets it in the menu with no code change — which is
   the whole reason `openStatusCodes` stopped being a literal (57a-i / 67 §3).
   `waiting` is back in it, because §1 withdraws C58; see StatusPill.tsx.

   `archived` IS EXCLUDED BY NAME, and that is deliberate rather than a missing
   flag. The taxonomy's `meta.terminal` cannot answer this question: MEASURED,
   `TASK_STATUS_TERMINAL` (src/utils/taxonomyMap.ts) declares `completed`,
   `cancelled` AND `archived` terminal, so a terminality test would drop all
   three and the menu would lose two statuses Laurent asked for. What makes
   `archived` different is not that the task is over — it is that archiving is
   136 §2's affordance, which renders only once the task carries a REASON
   (a terminal status that is not `archived`). A pill that could set `archived`
   directly would walk straight past that rule, so the one exclusion is stated
   here, by name, next to the sentence that explains it. */
export const ARCHIVED_STATUS = "archived";

export function pickableStatusCodes(statusEntries?: unknown): string[] {
  return ladderCodes(statusEntries).filter((c) => c !== ARCHIVED_STATUS);
}

/* BRIEF 136 §2 [RULED 2026-08-25, Laurent] — WHEN A TASK MAY BE ARCHIVED: "in
   Unlock mode, the archive icon should appear only when status is cancelled or
   completed (so one cannot archive without mentioning why)."

   THE FLAG, MINUS THE ONE CODE — measured, and the brief asks which it is.
   `meta.terminal` IS what "cancelled or completed" means here: on today's seed
   `TASK_STATUS_TERMINAL` declares exactly `completed`, `cancelled` and
   `archived`, so terminal-minus-archived is Laurent's pair with nothing added
   and nothing left out. Reading the flag rather than the pair is what makes the
   rule survive a firm's seventh status: a terminal one it adds ("obsolete",
   "withdrawn") is a reason in exactly the sense the ruling means, and gets the
   icon with no code change. A hand-written `["cancelled","completed"]` would be
   a SECOND declaration of the fact `isTerminalStatus` owns — the split 57a-i /
   67 §3 removed from `OPEN_ST`, reintroduced one file over.

   `archived` IS TERMINAL TOO AND MUST NOT QUALIFY, which is the whole reason
   this is not simply `isTerminalStatus`. It is the DESTINATION, not a reason:
   an archived task offering "archive" again says nothing and does nothing.
   It is the same exclusion §1's menu makes, so the two share one constant —
   and together they close the loop the ruling describes. §1 removes `archived`
   from the only control that could set a status directly; §2 makes the icon
   that remains wait for a reason. */
export function isArchivableStatus(
  status: string | null | undefined,
  statusEntries?: unknown,
): boolean {
  return status !== ARCHIVED_STATUS && isTerminalStatus(status, statusEntries);
}

// The predicate every surface is now downstream of.
export function taskIsOpen(
  task: CountableTask | null | undefined,
  statusEntries?: unknown,
): boolean {
  return !!task && !isTerminalStatus(task.status, statusEntries);
}

// The five dashboard counters, in one pass, from one predicate.
//
// `open` is the ONLY counter that does not depend on a date bound, which is
// what lets the two case lists call this exact function with nothing but
// `statusEntries` and read `.open`. The other four come back 0 when their
// bound is absent — a caller that wants them passes the dates.
export function countTasks(
  tasks: readonly CountableTask[] | null | undefined,
  opts: TaskCountOptions = {},
): TaskCounts {
  const {
    viewerId = null,
    statusEntries = null,
    todayISO = null,
    weekStartISO = null,
    weekEndISO = null,
  } = opts;
  const rows = Array.isArray(tasks) ? tasks : [];
  const mine =
    viewerId != null ? rows.filter((t) => taskIsMine(t, viewerId)) : rows;
  const inWeek = (d: string | null | undefined) =>
    !!d && !!weekStartISO && !!weekEndISO && d >= weekStartISO && d <= weekEndISO;

  const counts: TaskCounts = {
    open: 0,
    dueThisWeek: 0,
    overdue: 0,
    inProgress: 0,
    completedThisWeek: 0,
  };
  for (const t of mine) {
    if (!t) continue;
    const terminal = isTerminalStatus(t.status, statusEntries);
    if (!terminal) counts.open++;
    if (t.status === "inprogress") counts.inProgress++;
    const due = viewerDueDate(t, viewerId);
    if (inWeek(due)) counts.dueThisWeek++;
    if (due && todayISO && due < todayISO && !terminal) counts.overdue++;
    if (t.status === "completed" && inWeek(t.completedDate))
      counts.completedThisWeek++;
  }
  return counts;
}
